import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { 
  Phone, 
  Mail, 
  Clock, 
  Star, 
  ChevronRight, 
  Menu, 
  X, 
  CheckCircle2,
  Award,
  ShieldCheck,
  Sparkles,
  Instagram,
  ArrowRight,
  Quote,
  Eye
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets from design guidelines
const ASSETS = {
  hero: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/0em524ii_WhatsApp%20Image%202026-03-18%20at%2018.53.10.jpeg",
  interlock: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/tod0879u_WhatsApp%20Image%202026-03-18%20at%2018.53.09-4.jpeg",
  deck: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/c7w7oeqq_WhatsApp%20Image%202026-03-18%20at%2018.53.09-2.jpeg",
  steps: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/pow8zju0_WhatsApp%20Image%202026-03-18%20at%2018.53.09.jpeg",
  fence: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/1jsoqigp_WhatsApp%20Image%202026-03-18%20at%2018.53.09-3.jpeg"
};

// Service Categories
const SERVICE_CATEGORIES = [
  {
    title: "Hardscaping",
    image: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/hopa1zsk_WhatsApp%20Image%202026-04-03%20at%2016.16.58.jpeg",
    services: [
      "Interlocking (driveways, patios, walkways)",
      "Natural stone (flagstone, porches, steps)",
      "Retaining walls",
      "Concrete work (pads, stairs, curbs)",
      "Pool surroundings / pool coping",
      "Outdoor steps & entrances",
      "Paver repairs & relaying"
    ]
  },
  {
    title: "Landscaping",
    image: ASSETS.fence,
    services: [
      "Sod installation",
      "Artificial turf",
      "Planting (trees, shrubs, flowers)",
      "Garden design & flower beds",
      "Mulching & soil installation",
      "Lawn leveling / grading"
    ]
  },
  {
    title: "Woodwork & Structures",
    image: ASSETS.deck,
    services: [
      "Deck construction (pressure treated / composite)",
      "Pergolas",
      "Privacy screens",
      "Gazebos",
      "Custom outdoor structures"
    ]
  },
  {
    title: "Fencing",
    image: ASSETS.steps,
    services: [
      "Wood fencing (pressure treated / cedar)",
      "Composite fencing",
      "Fence repair & replacement",
      "Gates"
    ]
  },
  {
    title: "Pool Services",
    image: ASSETS.hero,
    services: [
      "In-ground pool installation",
      "Concrete / vinyl liner pools",
      "Pool demolition & rebuild"
    ]
  }
];

const REVIEWS = [
  {
    text: "We entrusted Bonsaii Landscaping with a large project including fence installation, retaining wall, pavers, and patio. Outstanding work and attention to detail.",
    author: "Michael R.",
    rating: 5
  },
  {
    text: "Andrei and his team did a fantastic job installing interlocking paving slabs and steps. Everything went smoothly from quote to completion.",
    author: "Sarah T.",
    rating: 5
  },
  {
    text: "The stairs and railings look fantastic. Huge improvement and completed exactly on schedule. Highly recommend their craftsmanship.",
    author: "David K.",
    rating: 5
  }
];

const TRUST_ITEMS = [
  { icon: Star, title: "4.9 Star Reputation", description: "Consistently excellent reviews" },
  { icon: Award, title: "Trusted Craftsmanship", description: "Premium quality materials" },
  { icon: Clock, title: "On-Time Completion", description: "We respect your schedule" },
  { icon: Sparkles, title: "Clean Premium Finishes", description: "Attention to every detail" },
  { icon: ShieldCheck, title: "Reliable Communication", description: "Transparent throughout" }
];

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/x7pu8nt9_WhatsApp%20Image%202026-03-18%20at%2019.10.26.jpeg";

// Navigation Component
const Navigation = ({ scrolled, onMenuClick, mobileMenuOpen }) => (
  <nav className={`nav-luxury ${scrolled ? "nav-scrolled" : ""}`} data-testid="navigation">
    <div className="container-luxury">
      <div className="flex items-center justify-between h-20">
        <a href="#hero" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Bonsaii Landscaping" className="h-12 w-12 rounded-full object-cover" />
          <span className="font-serif text-xl md:text-2xl font-semibold tracking-tight">BONSAII</span>
        </a>
        
        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8">
          {["About", "Services", "Portfolio", "Contact"].map((item) => (
            <a
              key={item}
              href={`#${item.toLowerCase()}`}
              className="text-xs uppercase tracking-[0.15em] text-white/70 hover:text-white transition-colors duration-300"
              data-testid={`nav-${item.toLowerCase()}`}
            >
              {item}
            </a>
          ))}
          <a 
            href="tel:6477834478" 
            className="btn-outline flex items-center gap-2"
            data-testid="nav-call-btn"
          >
            <Phone size={14} />
            <span>(647) 783-4478</span>
          </a>
        </div>
        
        {/* Mobile Menu Button */}
        <button 
          className="lg:hidden p-2"
          onClick={onMenuClick}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
    </div>
  </nav>
);

// Mobile Menu Component
const MobileMenu = ({ isOpen, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        <motion.div 
          className="fixed inset-0 bg-black/60 z-[150]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />
        <motion.div 
          className="mobile-menu open"
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "tween", duration: 0.3 }}
        >
          <button 
            onClick={onClose}
            className="absolute top-6 right-6"
            data-testid="mobile-menu-close"
          >
            <X size={24} />
          </button>
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="Bonsaii" className="h-14 w-14 rounded-full object-cover" />
              <span className="font-serif text-2xl">BONSAII</span>
            </div>
            {["About", "Services", "Portfolio", "Contact"].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                onClick={onClose}
                className="font-serif text-2xl hover:text-[#C5A059] transition-colors"
                data-testid={`mobile-nav-${item.toLowerCase()}`}
              >
                {item}
              </a>
            ))}
            <div className="mt-8 pt-8 border-t border-white/10">
              <a href="tel:6477834478" className="flex items-center gap-3 text-[#C5A059]">
                <Phone size={20} />
                <span>(647) 783-4478</span>
              </a>
            </div>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

// Hero Section
const HeroSection = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  
  return (
    <section id="hero" className="hero-section" data-testid="hero-section">
      <motion.div 
        className="absolute inset-0"
        style={{ y }}
      >
        <img 
          src={ASSETS.hero} 
          alt="Luxury landscaping"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="hero-overlay" />
      
      <motion.div 
        className="hero-content container-luxury w-full"
        style={{ opacity }}
      >
        <motion.div 
          className="max-w-4xl"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.p 
            className="caption-text mb-6"
            variants={fadeInUp}
          >
            Premium Landscaping Services
          </motion.p>
          
          <motion.h1 
            className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.1] mb-8"
            variants={fadeInUp}
          >
            Toronto's Most Trusted{" "}
            <span className="block text-[#C5A059]">Landscaping Company</span>
            Elevating Outdoor Living to Another Level
          </motion.h1>
          
          <motion.p 
            className="text-lg md:text-xl text-white/70 max-w-2xl mb-12 leading-relaxed"
            variants={fadeInUp}
          >
            Premium landscaping craftsmanship specializing in patios, retaining walls, 
            interlock, fences, stairs, and outdoor transformations.
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-4"
            variants={fadeInUp}
          >
            <a 
              href="#contact" 
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="hero-estimate-btn"
            >
              Get Free Estimate
              <ArrowRight size={16} />
            </a>
            <a 
              href="#portfolio" 
              className="btn-outline inline-flex items-center justify-center gap-2"
              data-testid="hero-portfolio-btn"
            >
              View Our Work
              <ChevronRight size={16} />
            </a>
          </motion.div>
        </motion.div>
      </motion.div>
      
      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 rounded-full border border-white/30 flex items-start justify-center p-2">
          <motion.div 
            className="w-1 h-2 bg-white/50 rounded-full"
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const ref = useRef(null);
  
  return (
    <section id="about" className="section-spacing relative" data-testid="about-section">
      <div className="container-luxury">
        <motion.div 
          ref={ref}
          className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <div>
            <p className="caption-text mb-4">About Us</p>
            <div className="gold-line mb-8" />
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight mb-8">
              Crafting Exceptional
              <span className="block text-[#C5A059]">Outdoor Experiences</span>
            </h2>
            <p className="text-white/60 text-lg leading-relaxed mb-8">
              Bonsaii Landscaping delivers premium outdoor transformations with precision, 
              craftsmanship, and attention to detail. From elegant patios to retaining walls 
              and custom hardscaping, every project is designed to increase beauty, function, 
              and long-term value.
            </p>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <span className="font-serif text-4xl text-[#C5A059]">150+</span>
                <p className="text-xs uppercase tracking-widest text-white/50 mt-2">Projects</p>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div className="text-center">
                <span className="font-serif text-4xl text-[#C5A059]">4.9</span>
                <p className="text-xs uppercase tracking-widest text-white/50 mt-2">Rating</p>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div className="text-center">
                <span className="font-serif text-4xl text-[#C5A059]">100%</span>
                <p className="text-xs uppercase tracking-widest text-white/50 mt-2">Satisfaction</p>
              </div>
            </div>
          </div>
          
          <div className="relative">
            <div className="aspect-[4/5] overflow-hidden">
              <img 
                src={ASSETS.deck} 
                alt="Premium craftsmanship"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#1A3C2F] p-6 md:p-8">
              <p className="font-serif text-2xl md:text-3xl text-white">10+ Years</p>
              <p className="text-xs uppercase tracking-widest text-white/60 mt-1">Of Excellence</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  return (
    <section id="services" className="section-spacing bg-[#0D0D0D]" data-testid="services-section">
      <div className="container-luxury">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="caption-text mb-4">What We Do</p>
          <div className="gold-line mx-auto mb-8" />
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl">
            Our Premium <span className="text-[#C5A059]">Services</span>
          </h2>
        </motion.div>
        
        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          {SERVICE_CATEGORIES.map((category, index) => (
            <motion.div
              key={category.title}
              className={`group relative overflow-hidden cursor-pointer ${index === 0 ? "lg:col-span-2 lg:row-span-2" : ""}`}
              variants={fadeInUp}
              onClick={() => setSelectedCategory(category)}
              data-testid={`service-card-${index}`}
            >
              {/* Image */}
              <div className={`relative ${index === 0 ? "aspect-[16/10]" : "aspect-[4/3]"} overflow-hidden`}>
                <img 
                  src={category.image} 
                  alt={category.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent group-hover:from-black/95 transition-all duration-500" />
                
                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                  <h3 className="font-serif text-2xl md:text-3xl text-white mb-2 group-hover:text-[#C5A059] transition-colors duration-300">{category.title}</h3>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-[#C5A059] uppercase tracking-widest">{category.services.length} Services</p>
                    <span className="text-white/50 group-hover:text-[#C5A059] group-hover:translate-x-1 transition-all duration-300">→</span>
                  </div>
                </div>
                
                {/* Border animation on hover */}
                <div className="absolute inset-0 border border-transparent group-hover:border-[#C5A059]/50 transition-colors duration-500" />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
      
      {/* Service Modal */}
      <AnimatePresence>
        {selectedCategory && (
          <motion.div 
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedCategory(null)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />
            
            {/* Modal Content */}
            <motion.div 
              className="relative bg-[#0D0D0D] border border-white/10 max-w-lg w-full max-h-[80vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={selectedCategory.image} 
                  alt={selectedCategory.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-transparent to-transparent" />
                
                {/* Close button */}
                <button 
                  onClick={() => setSelectedCategory(null)}
                  className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-[#C5A059] transition-colors"
                  data-testid="modal-close"
                >
                  <X size={20} />
                </button>
              </div>
              
              {/* Content */}
              <div className="p-8">
                <h3 className="font-serif text-3xl text-[#C5A059] mb-6">{selectedCategory.title}</h3>
                
                <ul className="space-y-4">
                  {selectedCategory.services.map((service, sIndex) => (
                    <motion.li 
                      key={sIndex}
                      className="flex items-center gap-4 text-white/90 pb-4 border-b border-white/5 last:border-0"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: sIndex * 0.05, duration: 0.3 }}
                    >
                      <span className="w-2 h-2 bg-[#C5A059] rounded-full flex-shrink-0" />
                      <span className="text-base">{service}</span>
                    </motion.li>
                  ))}
                </ul>
                
                <a 
                  href="#contact" 
                  onClick={() => setSelectedCategory(null)}
                  className="btn-primary w-full flex items-center justify-center gap-2 mt-8"
                >
                  Get Free Estimate
                  <ArrowRight size={16} />
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseSection = () => (
  <section className="section-spacing" data-testid="why-section">
    <div className="container-luxury">
      <motion.div 
        className="grid lg:grid-cols-2 gap-12 lg:gap-20"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div>
          <p className="caption-text mb-4">Why Choose Us</p>
          <div className="gold-line mb-8" />
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight mb-8">
            Why Clients Choose
            <span className="block text-[#C5A059]">Bonsaii Landscaping</span>
          </h2>
          <p className="text-white/60 text-lg leading-relaxed">
            We combine artistry with engineering to create outdoor spaces that stand the test 
            of time. Our commitment to excellence shows in every stone we lay and every 
            project we complete.
          </p>
        </div>
        
        <div className="space-y-4">
          {TRUST_ITEMS.map((item, index) => (
            <motion.div
              key={item.title}
              className="trust-indicator"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              data-testid={`trust-item-${index}`}
            >
              <div className="trust-icon">
                <item.icon size={24} />
              </div>
              <div>
                <h4 className="font-serif text-lg">{item.title}</h4>
                <p className="text-sm text-white/50">{item.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

// Our Promise Section
const PromiseSection = () => (
  <section className="py-20 md:py-28 relative overflow-hidden" data-testid="promise-section">
    <div className="absolute inset-0 bg-[#1A3C2F]" />
    <div className="container-luxury relative z-10">
      <motion.div 
        className="text-center max-w-4xl mx-auto"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <motion.div
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ type: "spring", duration: 0.6 }}
          className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-8"
        >
          <ShieldCheck size={40} className="text-[#C5A059]" />
        </motion.div>
        <p className="caption-text mb-4 text-white/60">Our Promise</p>
        <div className="gold-line mx-auto mb-8" />
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight text-white mb-12">
          We don't leave until you're{" "}
          <span className="text-[#C5A059]">100% satisfied</span>
        </h2>
        
        {/* Trust Badges */}
        <motion.div 
          className="flex flex-wrap justify-center gap-4 md:gap-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          {[
            "Fully Insured",
            "Free Estimates",
            "Satisfaction Guaranteed",
            "Local Toronto Experts"
          ].map((badge, index) => (
            <div 
              key={index}
              className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-5 py-3"
            >
              <CheckCircle2 size={18} className="text-[#C5A059]" />
              <span className="text-sm text-white font-medium">{badge}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// Reviews Section
const ReviewsSection = () => (
  <section className="section-spacing bg-[#0D0D0D]" data-testid="reviews-section">
    <div className="container-luxury">
      <motion.div 
        className="text-center mb-16"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <p className="caption-text mb-4">Testimonials</p>
        <div className="gold-line mx-auto mb-8" />
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl">
          What Our <span className="text-[#C5A059]">Clients Say</span>
        </h2>
      </motion.div>
      
      <div className="grid md:grid-cols-3 gap-6">
        {REVIEWS.map((review, index) => (
          <motion.div
            key={index}
            className="testimonial-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1, duration: 0.6 }}
            data-testid={`review-card-${index}`}
          >
            <Quote className="text-[#C5A059] mb-4" size={32} />
            <div className="flex gap-1 mb-4">
              {[...Array(review.rating)].map((_, i) => (
                <Star key={i} size={16} className="star-gold fill-current" />
              ))}
            </div>
            <p className="text-white/70 leading-relaxed mb-6 font-light italic">
              "{review.text}"
            </p>
            <p className="font-serif text-lg">{review.author}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Portfolio Categories
const PORTFOLIO_CATEGORIES = [
  { id: "all", name: "All Projects" },
  { id: "interlocking", name: "Interlocking Stone Work" },
  { id: "deck", name: "Wood & Composite Deck" },
  { id: "fence", name: "Composite & Wood Fence" },
  { id: "lighting", name: "Lighting" },
  { id: "sod", name: "Sod & Grading" },
  { id: "gardening", name: "Gardening" },
  { id: "natural", name: "Natural Stone Work" }
];

const PORTFOLIO_IMAGES = [
  // Interlocking Stone Work
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/7khyzp66_WhatsApp%20Image%202026-04-03%20at%2016.16.58-2.jpeg", category: "interlocking", title: "Interlock Driveway" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/jdc1mdph_WhatsApp%20Image%202026-04-03%20at%2016.16.58-3.jpeg", category: "interlocking", title: "Stone Walkway" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/gdy98q05_WhatsApp%20Image%202026-04-03%20at%2016.16.58-4.jpeg", category: "interlocking", title: "Front Yard Landscaping" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/thwq3h91_WhatsApp%20Image%202026-04-03%20at%2016.16.58.jpeg", category: "interlocking", title: "Premium Driveway" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/8lpgx2d9_WhatsApp%20Image%202026-04-03%20at%2016.24.37.jpeg", category: "interlocking", title: "Backyard Paver Patio" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/sgffd45y_WhatsApp%20Image%202026-04-03%20at%2016.22.23.jpeg", category: "interlocking", title: "Front Entrance Walkway" },
  // Wood & Composite Deck
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/9ovspij2_WhatsApp%20Image%202026-04-03%20at%2016.20.04-2.jpeg", category: "deck", title: "Composite Deck with Steps" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/02e4m88p_WhatsApp%20Image%202026-04-03%20at%2016.20.04-3.jpeg", category: "deck", title: "Gazebo Deck" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/c67z3cpn_WhatsApp%20Image%202026-04-03%20at%2016.20.04-4.jpeg", category: "deck", title: "Composite Stairs & Railing" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/tevsjqh7_WhatsApp%20Image%202026-04-03%20at%2016.20.04.jpeg", category: "deck", title: "Pressure Treated Deck" },
  // Composite & Wood Fence
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/7y5uiixw_WhatsApp%20Image%202026-04-03%20at%2016.20.26.jpeg", category: "fence", title: "Composite Privacy Fence" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/x4t4fbrt_WhatsApp%20Image%202026-04-03%20at%2016.20.25.jpeg", category: "fence", title: "Horizontal Cedar Fence" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/qn7t6kq2_WhatsApp%20Image%202026-04-03%20at%2016.20.25-3.jpeg", category: "fence", title: "Modern Composite Fence" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/37iltomn_WhatsApp%20Image%202026-04-03%20at%2016.20.25-2.jpeg", category: "fence", title: "Wood Privacy Fence" },
  // Lighting
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/cyzaks5o_WhatsApp%20Image%202026-04-03%20at%2016.21.07-2.jpeg", category: "lighting", title: "Backyard Patio Lighting" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/ra3llxol_WhatsApp%20Image%202026-04-03%20at%2016.21.07-3.jpeg", category: "lighting", title: "Driveway Accent Lights" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/os6b9i9v_WhatsApp%20Image%202026-04-03%20at%2016.21.07.jpeg", category: "lighting", title: "Evening Garden Ambiance" },
  // Sod & Grading
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/2r3s8448_WhatsApp%20Image%202026-04-03%20at%2016.22.06.jpeg", category: "sod", title: "Front Yard Sod Installation" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/7q5x3a4a_WhatsApp%20Image%202026-04-03%20at%2016.22.06-2.jpeg", category: "sod", title: "Backyard Grading & Sod" },
  // Gardening
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/lngvph30_WhatsApp%20Image%202026-04-03%20at%2016.28.39.jpeg", category: "gardening", title: "Japanese Garden Design" },
  // Natural Stone Work
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/ai09rwld_WhatsApp%20Image%202026-04-03%20at%2016.21.30-2.jpeg", category: "natural", title: "Flagstone Patio Night" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/ffnqx8nr_WhatsApp%20Image%202026-04-03%20at%2016.21.30.jpeg", category: "natural", title: "Natural Stone Entrance" },
  { src: "https://customer-assets.emergentagent.com/job_premium-landscaping/artifacts/flzu0nls_WhatsApp%20Image%202026-04-03%20at%2016.22.35.jpeg", category: "natural", title: "Stone Steps & Railings" },
  { src: ASSETS.hero, category: "natural", title: "Luxury Stone Patio" },
  { src: ASSETS.steps, category: "natural", title: "Stone Steps" }
];

// Portfolio Section
const PortfolioSection = ({ onImageClick }) => {
  const [activeCategory, setActiveCategory] = useState("all");
  
  const filteredImages = activeCategory === "all" 
    ? PORTFOLIO_IMAGES 
    : PORTFOLIO_IMAGES.filter(img => img.category === activeCategory);
  
  return (
    <section id="portfolio" className="section-spacing" data-testid="portfolio-section">
      <div className="container-luxury">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="caption-text mb-4">Our Work</p>
          <div className="gold-line mx-auto mb-8" />
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl">
            Project <span className="text-[#C5A059]">Gallery</span>
          </h2>
        </motion.div>
        
        {/* Category Filter */}
        <motion.div 
          className="flex flex-wrap justify-center gap-3 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          {PORTFOLIO_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-6 py-3 text-xs uppercase tracking-widest font-medium transition-all duration-300 ${
                activeCategory === cat.id 
                  ? "bg-[#C5A059] text-black" 
                  : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10"
              }`}
              data-testid={`filter-${cat.id}`}
            >
              {cat.name}
            </button>
          ))}
        </motion.div>
        
        {/* Gallery Grid */}
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"
          layout
        >
          <AnimatePresence mode="popLayout">
            {filteredImages.map((img, index) => (
              <motion.div
                key={img.src}
                className="gallery-item aspect-square cursor-pointer group"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                layout
                onClick={() => onImageClick(img.src)}
                data-testid={`gallery-item-${index}`}
              >
                <img src={img.src} alt={img.title} className="w-full h-full object-cover" />
                <div className="gallery-overlay">
                  <div className="text-center">
                    <Eye size={28} className="text-white mx-auto mb-2" />
                    <p className="text-sm text-white/80">{img.title}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => (
  <section className="cta-section py-24 md:py-32" data-testid="cta-section">
    <div className="cta-bg" />
    <div className="container-luxury relative z-10">
      <motion.div 
        className="text-center max-w-3xl mx-auto"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight mb-8">
          Transform Your Outdoor Space Into
          <span className="block text-[#C5A059]">Something Exceptional</span>
        </h2>
        <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto">
          Let us bring your vision to life with premium craftsmanship and attention to detail.
        </p>
        <a 
          href="#contact" 
          className="btn-primary inline-flex items-center gap-2"
          data-testid="cta-estimate-btn"
        >
          Request Your Estimate
          <ArrowRight size={16} />
        </a>
      </motion.div>
    </div>
  </section>
);

// All Services for dropdown
const ALL_SERVICES = [
  // Hardscaping
  "Interlocking (driveways, patios, walkways)",
  "Natural stone (flagstone, porches, steps)",
  "Retaining walls",
  "Concrete work (pads, stairs, curbs)",
  "Pool surroundings / pool coping",
  "Outdoor steps & entrances",
  "Paver repairs & relaying",
  // Landscaping
  "Sod installation",
  "Artificial turf",
  "Planting (trees, shrubs, flowers)",
  "Garden design & flower beds",
  "Mulching & soil installation",
  "Lawn leveling / grading",
  // Woodwork
  "Deck construction (pressure treated / composite)",
  "Pergolas",
  "Privacy screens",
  "Gazebos",
  "Custom outdoor structures",
  // Fencing
  "Wood fencing (pressure treated / cedar)",
  "Composite fencing",
  "Fence repair & replacement",
  "Gates",
  // Pool
  "In-ground pool installation",
  "Concrete / vinyl liner pools",
  "Pool demolition & rebuild"
];

// Contact Section with Web3Forms
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  
  // Web3Forms Access Key
  const WEB3FORMS_KEY = "6ac74fe5-96d2-4f0e-913c-c30d154bfde5";
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Anti-spam: honeypot check
    if (honeypot) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `Nouvelle demande de soumission - ${formData.service}`,
          from_name: "Bonsaii Landscaping Website",
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          service: formData.service,
          message: formData.message,
          // Send copy to client
          cc: formData.email,
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSubmitted(true);
        setFormData({ name: "", phone: "", email: "", service: "", message: "" });
        toast.success("Merci, votre demande a bien été envoyée!");
      } else {
        toast.error("Erreur lors de l'envoi. Veuillez nous appeler directement.");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi. Veuillez nous appeler directement.");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <section id="contact" className="section-spacing bg-[#0D0D0D]" data-testid="contact-section">
      <div className="container-luxury">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="caption-text mb-4">Request a Quote</p>
            <div className="gold-line mb-8" />
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight mb-8">
              Get Your
              <span className="block text-[#C5A059]">Free Estimate</span>
            </h2>
            <p className="text-white/60 text-lg leading-relaxed mb-10">
              Fill out the form and we will contact you shortly to discuss your project.
            </p>
            
            <div className="space-y-6">
              <a 
                href="tel:6477834478" 
                className="flex items-center gap-4 text-white/70 hover:text-[#C5A059] transition-colors"
                data-testid="contact-phone"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-[#1A3C2F]/30 border border-[#1A3C2F]">
                  <Phone size={22} className="text-[#C5A059]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Phone</p>
                  <p className="text-xl font-medium">(647) 783-4478</p>
                </div>
              </a>
              
              <a 
                href="mailto:bonsaii.landscaping@gmail.com" 
                className="flex items-center gap-4 text-white/70 hover:text-[#C5A059] transition-colors"
                data-testid="contact-email"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-[#1A3C2F]/30 border border-[#1A3C2F]">
                  <Mail size={22} className="text-[#C5A059]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Email</p>
                  <p className="text-lg">bonsaii.landscaping@gmail.com</p>
                </div>
              </a>
              
              <div className="flex items-center gap-4 text-white/70">
                <div className="w-14 h-14 flex items-center justify-center bg-[#1A3C2F]/30 border border-[#1A3C2F]">
                  <Clock size={22} className="text-[#C5A059]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Business Hours</p>
                  <p className="text-lg">Mon - Sun: 9AM - 5PM</p>
                </div>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {submitted ? (
              <div className="contact-form flex flex-col items-center justify-center text-center min-h-[500px]">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="w-20 h-20 rounded-full bg-[#1A3C2F] flex items-center justify-center mb-6"
                >
                  <CheckCircle2 size={40} className="text-[#C5A059]" />
                </motion.div>
                <h3 className="font-serif text-2xl md:text-3xl mb-4">Thank You!</h3>
                <p className="text-white/60 text-lg max-w-md">
                  Your request has been sent successfully. We will contact you shortly to discuss your project.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="btn-outline mt-8"
                  data-testid="new-request-btn"
                >
                  New Request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
                <div className="space-y-6">
                  {/* Honeypot anti-spam field - hidden */}
                  <input
                    type="text"
                    name="honeypot"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    style={{ display: "none" }}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                  
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/40 mb-2 block">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-minimal"
                      required
                      minLength={2}
                      data-testid="input-name"
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/40 mb-2 block">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        placeholder="(xxx) xxx-xxxx"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="input-minimal"
                        required
                        minLength={10}
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/40 mb-2 block">
                        Email *
                      </label>
                      <input
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="input-minimal"
                        required
                        data-testid="input-email"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/40 mb-2 block">
                      Service Requested *
                    </label>
                    <select
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      className="input-minimal bg-transparent cursor-pointer"
                      required
                      data-testid="input-service"
                    >
                      <option value="" disabled className="bg-[#0A0A0A]">Select a service</option>
                      <optgroup label="Hardscaping" className="bg-[#0A0A0A]">
                        <option value="Interlocking (driveways, patios, walkways)" className="bg-[#0A0A0A]">Interlocking (driveways, patios, walkways)</option>
                        <option value="Natural stone (flagstone, porches, steps)" className="bg-[#0A0A0A]">Natural stone (flagstone, porches, steps)</option>
                        <option value="Retaining walls" className="bg-[#0A0A0A]">Retaining walls</option>
                        <option value="Concrete work (pads, stairs, curbs)" className="bg-[#0A0A0A]">Concrete work (pads, stairs, curbs)</option>
                        <option value="Pool surroundings / pool coping" className="bg-[#0A0A0A]">Pool surroundings / pool coping</option>
                        <option value="Outdoor steps & entrances" className="bg-[#0A0A0A]">Outdoor steps & entrances</option>
                        <option value="Paver repairs & relaying" className="bg-[#0A0A0A]">Paver repairs & relaying</option>
                      </optgroup>
                      <optgroup label="Landscaping" className="bg-[#0A0A0A]">
                        <option value="Sod installation" className="bg-[#0A0A0A]">Sod installation</option>
                        <option value="Artificial turf" className="bg-[#0A0A0A]">Artificial turf</option>
                        <option value="Planting (trees, shrubs, flowers)" className="bg-[#0A0A0A]">Planting (trees, shrubs, flowers)</option>
                        <option value="Garden design & flower beds" className="bg-[#0A0A0A]">Garden design & flower beds</option>
                        <option value="Mulching & soil installation" className="bg-[#0A0A0A]">Mulching & soil installation</option>
                        <option value="Lawn leveling / grading" className="bg-[#0A0A0A]">Lawn leveling / grading</option>
                      </optgroup>
                      <optgroup label="Woodwork & Structures" className="bg-[#0A0A0A]">
                        <option value="Deck construction (pressure treated / composite)" className="bg-[#0A0A0A]">Deck construction (pressure treated / composite)</option>
                        <option value="Pergolas" className="bg-[#0A0A0A]">Pergolas</option>
                        <option value="Privacy screens" className="bg-[#0A0A0A]">Privacy screens</option>
                        <option value="Gazebos" className="bg-[#0A0A0A]">Gazebos</option>
                        <option value="Custom outdoor structures" className="bg-[#0A0A0A]">Custom outdoor structures</option>
                      </optgroup>
                      <optgroup label="Fencing" className="bg-[#0A0A0A]">
                        <option value="Wood fencing (pressure treated / cedar)" className="bg-[#0A0A0A]">Wood fencing (pressure treated / cedar)</option>
                        <option value="Composite fencing" className="bg-[#0A0A0A]">Composite fencing</option>
                        <option value="Fence repair & replacement" className="bg-[#0A0A0A]">Fence repair & replacement</option>
                        <option value="Gates" className="bg-[#0A0A0A]">Gates</option>
                      </optgroup>
                      <optgroup label="Pool Services" className="bg-[#0A0A0A]">
                        <option value="In-ground pool installation" className="bg-[#0A0A0A]">In-ground pool installation</option>
                        <option value="Concrete / vinyl liner pools" className="bg-[#0A0A0A]">Concrete / vinyl liner pools</option>
                        <option value="Pool demolition & rebuild" className="bg-[#0A0A0A]">Pool demolition & rebuild</option>
                      </optgroup>
                      <optgroup label="Other" className="bg-[#0A0A0A]">
                        <option value="Multiple services" className="bg-[#0A0A0A]">Multiple services</option>
                        <option value="Other" className="bg-[#0A0A0A]">Other</option>
                      </optgroup>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/40 mb-2 block">
                      Message / Project Details *
                    </label>
                    <textarea
                      placeholder="Describe your project in detail..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="input-minimal min-h-[140px] resize-none"
                      required
                      minLength={10}
                      data-testid="input-message"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full flex items-center justify-center gap-3 py-5 text-sm"
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Request
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                  
                  <p className="text-xs text-white/30 text-center">
                    By submitting this form, you will receive a copy of your request by email.
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="footer-luxury py-12 md:py-16" data-testid="footer">
    <div className="container-luxury">
      <div className="flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Bonsaii" className="h-10 w-10 rounded-full object-cover" />
          <div>
            <h3 className="font-serif text-xl">BONSAII</h3>
            <p className="text-xs uppercase tracking-[0.2em] text-white/40">Landscaping</p>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          <a 
            href="tel:6477834478" 
            className="text-white/60 hover:text-[#C5A059] transition-colors text-sm"
          >
            (647) 783-4478
          </a>
          <a
            href="https://www.instagram.com/bonsaii.landscaping"
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 flex items-center justify-center border border-white/10 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
            data-testid="instagram-link"
          >
            <Instagram size={18} />
          </a>
        </div>
      </div>
      
      <div className="border-t border-white/5 mt-10 pt-10 text-center">
        <p className="text-xs text-white/30">
          © {new Date().getFullYear()} Bonsaii Landscaping. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Lightbox Component
const Lightbox = ({ image, onClose }) => (
  <AnimatePresence>
    {image && (
      <motion.div 
        className="lightbox-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="lightbox"
      >
        <motion.img 
          src={image} 
          alt="Portfolio"
          className="lightbox-image"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        />
        <button 
          className="absolute top-6 right-6 text-white/70 hover:text-white"
          onClick={onClose}
          data-testid="lightbox-close"
        >
          <X size={32} />
        </button>
      </motion.div>
    )}
  </AnimatePresence>
);

// Main App Component
function App() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  // Close mobile menu on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setMobileMenuOpen(false);
        setLightboxImage(null);
      }
    };
    
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);
  
  return (
    <div className="min-h-screen" data-testid="app-root">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#1C1C1E',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#F5F5F0'
          }
        }}
      />
      
      <Navigation 
        scrolled={scrolled} 
        onMenuClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        mobileMenuOpen={mobileMenuOpen}
      />
      
      <MobileMenu 
        isOpen={mobileMenuOpen} 
        onClose={() => setMobileMenuOpen(false)} 
      />
      
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <WhyChooseSection />
        <PromiseSection />
        <ReviewsSection />
        <PortfolioSection onImageClick={setLightboxImage} />
        <CTASection />
        <ContactSection />
      </main>
      
      <Footer />
      
      <Lightbox 
        image={lightboxImage} 
        onClose={() => setLightboxImage(null)} 
      />
    </div>
  );
}

export default App;
